/** The client crash-report endpoint: its caps, its report shape, and its two responses. */

/**
 * The **client crash-report** REST endpoint. The browser POSTs a
 * batch of {@link ClientErrorReport}s here; the server symbolicates each stack against the
 * bundle's sourcemap and appends the result to `<world>/client-errors.jsonl`.
 *
 * **REST, not the game socket, and deliberately unauthenticated.** The two are the same
 * decision: a `game_hello` needs an authenticated slot, so a crash during boot or login —
 * the class you most want to see — would have no way to report itself, and a crash bad
 * enough to kill the socket could never flush the queue it left behind. An open endpoint
 * costs us little here (the players are invited friends), and the disk
 * is protected by {@link CLIENT_ERROR_BATCH_CAP} + {@link CLIENT_ERROR_FIELD_CAP} instead
 * of by a credential.
 *
 * Answers `202 {@link ClientErrorAck}` on accept. It never answers an error the client is
 * expected to act on: a report about a broken client that itself needs error handling is a
 * loop, so a malformed batch is dropped with `400` and the client discards it either way.
 */
export const CLIENT_ERROR_PATH = "/api/client-error" as const;

/**
 * How a client error reached the reporter. Kept as a closed union rather than free text so
 * the log can be filtered by class — an `unhandledrejection` is a different debugging story
 * from a React render crash, and both are different from a plain `window.onerror`.
 */
export type ClientErrorSource = "window.onerror" | "unhandledrejection" | "react";

/**
 * The cap on a single batch POST. The client never sends more than this many reports at
 * once, and the server drops the overflow rather than rejecting the batch — a client that
 * has been offline through a long session should get *some* of its backlog in, not none.
 */
export const CLIENT_ERROR_BATCH_CAP = 20 as const;
/**
 * The cap on any single string field of a report (message, stack, componentStack, url,
 * userAgent). Truncation happens on the **client**, before the report is ever queued, so an
 * enormous stack can't blow the localStorage budget either — the server re-applies it only
 * because it does not trust the client.
 */
export const CLIENT_ERROR_FIELD_CAP = 8_000 as const;
/**
 * How many reports the client keeps in localStorage while it can't reach the server. A
 * bounded ring: at the cap the **oldest** is dropped, because a crash loop that fires
 * hundreds of identical errors should not evict the first, most-diagnostic one... and it
 * doesn't, because the queue also dedupes. See `frontend/src/lib/errorReporting/queue.ts`.
 */
export const CLIENT_ERROR_QUEUE_CAP = 50 as const;

/**
 * One client-side error as the browser reported it — **raw**, with the stack exactly as the
 * engine produced it (minified bundle URLs, line/col into the built asset). The client does
 * no symbolication: resolving a frame needs the 15 MB sourcemap, which the server already
 * has on disk and the browser very much should not download.
 */
export type ClientErrorReport = {
  /** `Date.now()` at capture on the client. Preserved through the queue, so an error that sat offline keeps its real time. */
  capturedAt: number;
  source: ClientErrorSource;
  /** The error's message. Always present — a thrown non-Error is stringified into it. */
  message: string;
  /** The raw stack, if the thrown value had one. Absent for a thrown string/object. */
  stack?: string;
  /** `location.href` at capture — which surface the user was on. */
  url?: string;
  userAgent?: string;
  /** The authenticated slot id, when the client knew one. Absent for a pre-login crash. */
  slotId?: string;
  /** React's `componentStack` — only ever set when `source` is `"react"`. */
  componentStack?: string;
};

/** The POST body: a batch of reports, oldest first. */
export type ClientErrorBatch = {
  reports: ClientErrorReport[];
};

/**
 * The accept response. `stored` may be **less** than the batch size — the server caps at
 * {@link CLIENT_ERROR_BATCH_CAP} and silently drops the overflow. The client treats any
 * `2xx` as "the whole batch is delivered" and clears it regardless: retrying the remainder
 * would need per-report acks, and losing the tail of a flood is not worth that machinery.
 */
export type ClientErrorAck = {
  stored: number;
};

/**
 * The **review** half of {@link CLIENT_ERROR_PATH}: `GET` returns the tail of the crash log.
 *
 * **GM-gated** via {@link HOST_KEY_HEADER}, unlike the POST — and the asymmetry is the point.
 * Writing a crash report is something a broken, unauthenticated client must be able to do;
 * *reading* the log back is an operator action, and the log contains other players' session
 * URLs and user agents. `?limit=<n>` bounds the tail (default {@link CLIENT_ERROR_TAIL_DEFAULT}).
 */
export const CLIENT_ERROR_TAIL_DEFAULT = 50 as const;

/**
 * The `GET {@link CLIENT_ERROR_PATH}` response. `records` are oldest→newest, matching how the
 * log reads on disk. Typed as `unknown` here because the stored shape (`ClientErrorRecord`,
 * with its symbolicated frames) is a **server** type — `shared/` deliberately doesn't know
 * about sourcemaps, and the CLI only ever formats these for a human.
 */
export type ClientErrorListResponse = {
  records: unknown[];
};